import { useEffect, useRef, forwardRef, useImperativeHandle, useState, memo } from 'react';
import { cn } from '@/lib/utils';
import { Play, Pause } from 'lucide-react';

declare global {
    interface Window {
        YT: any;
        onYouTubeIframeAPIReady: () => void;
    }
}

interface SecureVideoPlayerProps {
    videoUrl: string;
    userEmail?: string;
    className?: string;
    startTime?: number;
    onTimeUpdate?: (currentTime: number, duration: number) => void;
    onLoadedMetadata?: () => void;
    onEnded?: () => void;
    overlayImageUrl?: string;
    streamVideoId?: string;
}

const SecureVideoPlayer = forwardRef<HTMLVideoElement, SecureVideoPlayerProps>(({
    videoUrl,
    userEmail,
    className,
    startTime = 0,
    onTimeUpdate,
    onLoadedMetadata,
    onEnded,
    overlayImageUrl,
    streamVideoId
}, ref) => {
    const CLOUDFLARE_ACCOUNT_ID = import.meta.env.VITE_CLOUDFLARE_ACCOUNT_ID || 'ACCOUNT_ID_PLACEHOLDER';
    const containerRef = useRef<HTMLDivElement>(null);
    const internalVideoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<any>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isApiReady, setIsApiReady] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Forward the internal video ref to the parent if provided
    useImperativeHandle(ref, () => internalVideoRef.current as HTMLVideoElement);

    const isYouTube = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');

    // Load YouTube Iframe API
    useEffect(() => {
        if (!isYouTube) return;

        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = "https://www.youtube.com/iframe_api";
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

            window.onYouTubeIframeAPIReady = () => {
                setIsApiReady(true);
            };
        } else {
            setIsApiReady(true);
        }
    }, [isYouTube]);

    const onPlayerReady = (event: any) => {
        playerRef.current = event.target;
        setDuration(event.target.getDuration());
    };

    const onPlayerStateChange = (event: any) => {
        // YT.PlayerState: UNSTARTED (-1), ENDED (0), PLAYING (1), PAUSED (2), BUFFERING (3), CUED (5)
        const state = event.data;

        if (state === 1) { // PLAYING
            setIsPlaying(true);
        } else if (state === 2 || state === 3 || state === -1) { // PAUSED, BUFFERING, or UNSTARTED
            setIsPlaying(false);
        } else if (state === 0) { // ENDED
            setIsPlaying(false);
            onEnded?.();
        }
    };

    // Robust State Heartbeat (Safety Sync every 1s)
    useEffect(() => {
        let heartbeat: any;
        if (isYouTube && playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
            heartbeat = setInterval(() => {
                const realState = playerRef.current.getPlayerState();
                const isActuallyPlaying = realState === 1; // 1 is Playing

                // If mismatch found, force sync the UI state
                if (isActuallyPlaying !== isPlaying) {
                    setIsPlaying(isActuallyPlaying);
                }
            }, 1000);
        }
        return () => clearInterval(heartbeat);
    }, [isYouTube, isPlaying]);

    // Polling interval for time tracking
    useEffect(() => {
        let interval: any;
        if (isPlaying && playerRef.current) {
            interval = setInterval(() => {
                const time = playerRef.current.getCurrentTime();
                setCurrentTime(time);
                onTimeUpdate?.(time, duration);
            }, 100);
        }
        return () => clearInterval(interval);
    }, [isPlaying, duration]);

    const togglePlay = () => {
        if (!playerRef.current || typeof playerRef.current.getPlayerState !== 'function') return;

        const state = playerRef.current.getPlayerState();
        if (state === 1) { // Currently Playing
            playerRef.current.pauseVideo();
            setIsPlaying(false);
        } else { // Paused, Ended, or Cued
            playerRef.current.playVideo();
            setIsPlaying(true);
        }
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!playerRef.current || duration === 0) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const clickedValue = (x / rect.width) * duration;

        playerRef.current.seekTo(clickedValue, true);
        setCurrentTime(clickedValue);
    };

    const formatTime = (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);

        const parts = [];
        if (h > 0) parts.push(h.toString().padStart(2, '0'));
        parts.push(m.toString().padStart(2, '0'));
        parts.push(s.toString().padStart(2, '0'));

        return parts.join(':');
    };

    const getCloudflareUrl = (videoId: string) => {
        const params = new URLSearchParams({
            autoplay: 'false',
            muted: 'false',
            loop: 'false',
            poster: '',
            preload: 'metadata',
            controls: 'true'
        });

        if (startTime > 0) {
            params.set('startTime', Math.floor(startTime).toString());
        }

        return `https://customer-${CLOUDFLARE_ACCOUNT_ID}.cloudflarestream.com/${videoId}/iframe?${params.toString()}`;
    };

    const getYouTubeEmbedUrl = (url: string, startAt: number) => {
        let videoId = '';
        if (url.includes('embed/')) videoId = url.split('embed/')[1]?.split('?')[0];
        else if (url.includes('v=')) videoId = url.split('v=')[1]?.split('&')[0];
        else if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1]?.split('?')[0];

        // YouTube Setup with API and hidden controls
        // playsinline=1 and mute=0 are critical for manual playback with sound
        const params = new URLSearchParams({
            enablejsapi: '1',
            autoplay: '0',
            controls: '0',
            modestbranding: '1',
            rel: '0',
            showinfo: '0',
            iv_load_policy: '3',
            disablekb: '1',
            playsinline: '1',
            mute: '0',
            origin: window.location.origin,
            widget_referrer: window.location.href
        });

        if (startAt > 0) {
            params.set('start', Math.floor(startAt).toString());
        }

        return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    };

    useEffect(() => {
        // Global Right-click prevention
        const preventContextMenu = (e: MouseEvent) => e.preventDefault();

        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const isInspect = e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I') || (e.ctrlKey && e.key === 'U');
            const isCopy = e.ctrlKey && (e.key === 'c' || e.key === 'C');
            const isSave = e.ctrlKey && (e.key === 's' || e.key === 'S');

            if (isInspect || isCopy || isSave) {
                e.preventDefault();
                return false;
            }
        };

        document.addEventListener('contextmenu', preventContextMenu);
        window.addEventListener('keydown', handleGlobalKeyDown);

        return () => {
            document.removeEventListener('contextmenu', preventContextMenu);
            window.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, []);

    // Initialize YouTube Player
    useEffect(() => {
        if (isApiReady && isYouTube && !playerRef.current) {
            const timer = setTimeout(() => {
                const iframe = document.getElementById('courseVideo') as HTMLIFrameElement;
                if (iframe && window.YT && window.YT.Player) {
                    new window.YT.Player('courseVideo', {
                        events: {
                            'onReady': onPlayerReady,
                            'onStateChange': onPlayerStateChange
                        }
                    });
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isApiReady, isYouTube]);

    const renderPlayer = () => {
        return (
            <MemoizedBasePlayer
                isYouTube={isYouTube}
                videoUrl={videoUrl}
                streamVideoId={streamVideoId}
                startTime={startTime}
                getCloudflareUrl={getCloudflareUrl}
                getYouTubeEmbedUrl={getYouTubeEmbedUrl}
                internalVideoRef={internalVideoRef}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={onLoadedMetadata}
                onEnded={onEnded}
            />
        );
    };

    return (
        <div
            ref={containerRef}
            className={cn("video-root bg-[#001B3D] rounded-[20px] overflow-hidden shadow-premium border border-white/60 relative select-none group", className)}
            onContextMenu={(e) => e.preventDefault()}
            style={{
                position: 'relative',
                userSelect: 'none',
                WebkitUserSelect: 'none'
            }}
        >
            <div className="w-full h-full relative">
                {renderPlayer()}

                {/* Secure Overlay and Custom UI Controls */}
                {isYouTube && (
                    <div className="absolute inset-0 z-[2]">
                        {/* Custom Controls Bar */}
                        <div
                            className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 to-transparent flex flex-col gap-4 z-30"
                        >
                            {/* Progress Bar */}
                            <div
                                className="progress-container w-full h-[6px] bg-white/20 rounded-full cursor-pointer relative overflow-hidden"
                                onClick={handleProgressClick}
                                style={{ pointerEvents: 'auto' }}
                            >
                                <div
                                    className="progress-bar h-full transition-all duration-100"
                                    style={{
                                        width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                                        background: 'linear-gradient(90deg, #FFD700, #FFA500)'
                                    }}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <button
                                        id="playPauseBtn"
                                        onClick={togglePlay}
                                        style={{ pointerEvents: 'auto' }}
                                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-accent/20 flex items-center justify-center text-white transition-all"
                                    >
                                        {isPlaying ? (
                                            <Pause className="w-5 h-5 fill-current" />
                                        ) : (
                                            <Play className="w-5 h-5 fill-current ml-1" />
                                        )}
                                    </button>

                                    <span
                                        id="timeDisplay"
                                        className="text-[10px] font-black uppercase tracking-widest text-white/80"
                                    >
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Large Center Play Button */}
                        {!isPlaying && playerRef.current && (
                            <button
                                onClick={togglePlay}
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-accent/90 flex items-center justify-center text-primary shadow-2xl hover:scale-110 active:scale-95 transition-all z-20"
                                style={{ pointerEvents: 'auto' }}
                            >
                                <Play className="w-8 h-8 fill-current ml-1" />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Branding Layer Overlay */}
            <div
                className="video-secure-overlay-shell"
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 40,
                    pointerEvents: 'none',
                    userSelect: 'none'
                }}
                onContextMenu={(e) => e.preventDefault()}
            >
                <img
                    src="/tranding-overlay.png"
                    alt="Secure Player"
                    className="w-full h-full object-cover"
                    style={{
                        opacity: 0.4,
                        pointerEvents: 'none',
                        userSelect: 'none',
                        // @ts-ignore
                        WebkitUserDrag: 'none',
                        mixBlendMode: 'multiply'
                    }}
                    draggable={false}
                />

                {/* Watermark Email Overlay */}
                {userEmail && (
                    <div
                        className="watermark"
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '25px',
                            color: 'white',
                            opacity: 0.5,
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            zIndex: 50,
                            pointerEvents: 'none',
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                        }}
                    >
                        {userEmail}
                    </div>
                )}
            </div>

            {/* Deterrence Watermark */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none w-full text-center opacity-10">
                <div className="text-[10px] md:text-xl font-bold uppercase tracking-[0.3em] text-white select-none px-10 whitespace-nowrap">
                    RATCHAGAN ANALYSIS – PRIVATE COURSE
                </div>
            </div>
        </div>
    );
});

// Memoized Player Engine to prevent iframe reloads during progress saves
const MemoizedBasePlayer = memo(({
    isYouTube,
    videoUrl,
    streamVideoId,
    startTime,
    getCloudflareUrl,
    getYouTubeEmbedUrl,
    internalVideoRef,
    onTimeUpdate,
    onLoadedMetadata,
    onEnded
}: any) => {
    if (streamVideoId) {
        return (
            <div className="video-wrapper">
                <iframe
                    src={getCloudflareUrl(streamVideoId)}
                    className="video-frame pointer-events-auto"
                    frameBorder="0"
                    allowFullScreen
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    referrerPolicy="strict-origin-when-cross-origin"
                    onContextMenu={(e) => e.preventDefault()}
                ></iframe>
            </div>
        );
    }

    if (isYouTube) {
        return (
            <div className="video-wrapper">
                <iframe
                    id="courseVideo"
                    src={getYouTubeEmbedUrl(videoUrl, startTime)}
                    className="video-frame pointer-events-none"
                    frameBorder="0"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    onContextMenu={(e) => e.preventDefault()}
                ></iframe>
            </div>
        );
    }

    return (
        <div className="video-wrapper">
            <video
                ref={internalVideoRef}
                src={videoUrl}
                controls
                className="video-frame object-contain pointer-events-auto"
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={() => onTimeUpdate?.(internalVideoRef.current?.currentTime || 0, internalVideoRef.current?.duration || 0)}
                onLoadedMetadata={() => {
                    if (internalVideoRef.current && startTime > 0) {
                        internalVideoRef.current.currentTime = startTime;
                    }
                    onLoadedMetadata?.();
                }}
                onEnded={onEnded}
            />
        </div>
    );
}, (prev, next) => {
    // CRITICAL: Prevent iframe reload when startTime changes (which happens every 1s-10s)
    // Only re-render if the video source itself changes.
    return prev.videoUrl === next.videoUrl && prev.streamVideoId === next.streamVideoId;
});

SecureVideoPlayer.displayName = 'SecureVideoPlayer';

export default SecureVideoPlayer;
